import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    budget: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d53k6h7b58v6mebe8gb0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', service: '', budget: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className="py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-8 sm:gap-10 lg:gap-12">
          {/* Left Column - Contact Info */}
          <div className="lg:col-span-2 space-y-6 sm:space-y-8">
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
                Ready to Launch Your Website?
              </h2>
              <p className="text-base sm:text-lg text-gray-600">
                Get a free consultation and discover how we can help your business grow online
              </p>
            </div>

            {/* Contact Cards */}
            <div className="space-y-3 sm:space-y-4">
              <a href="mailto:info@outsmartwebs.com" className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer group">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-mail-line text-white text-xl sm:text-2xl"></i>
                </div>
                <div>
                  <div className="text-xs sm:text-sm text-gray-600">Email Us</div>
                  <div className="text-sm sm:text-base font-semibold text-gray-900">info@outsmartwebs.com</div>
                </div>
              </a>

              <a href="tel:+14317660666" className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer group">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-phone-line text-white text-xl sm:text-2xl"></i>
                </div>
                <div>
                  <div className="text-xs sm:text-sm text-gray-600">Call Us</div>
                  <div className="text-sm sm:text-base font-semibold text-gray-900">+1 (431) 766-0666</div>
                </div>
              </a>

              <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-white rounded-xl sm:rounded-2xl shadow-md">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-white text-xl sm:text-2xl"></i>
                </div>
                <div>
                  <div className="text-xs sm:text-sm text-gray-600">Business Hours</div>
                  <div className="text-sm sm:text-base font-semibold text-gray-900">Mon-Fri: 9AM - 6PM EST</div>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div>
              <div className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">Follow Us</div>
              <div className="flex gap-2 sm:gap-3">
                {[
                  { icon: 'ri-linkedin-fill', color: 'from-blue-600 to-blue-700' },
                  { icon: 'ri-twitter-x-fill', color: 'from-gray-800 to-gray-900' },
                  { icon: 'ri-instagram-fill', color: 'from-pink-600 to-purple-600' },
                  { icon: 'ri-facebook-fill', color: 'from-blue-500 to-blue-600' }
                ].map((social, index) => (
                  <a
                    key={index}
                    href="#"
                    className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br ${social.color} flex items-center justify-center text-white hover:scale-110 transition-transform cursor-pointer`}
                  >
                    <i className={`${social.icon} text-lg sm:text-xl`}></i>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Contact Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="bg-white rounded-2xl sm:rounded-3xl shadow-xl p-6 sm:p-8 lg:p-10 space-y-5 sm:space-y-6">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-b-2 border-gray-200 focus:border-cyan-500 outline-none transition-colors text-sm sm:text-base bg-transparent"
                  placeholder="John Doe"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-b-2 border-gray-200 focus:border-cyan-500 outline-none transition-colors text-sm sm:text-base bg-transparent"
                  placeholder="john@example.com"
                />
              </div>

              {/* Service Interest */}
              <div>
                <label htmlFor="service" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  What Type of Website Do You Need?
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-b-2 border-gray-200 focus:border-cyan-500 outline-none transition-colors text-sm sm:text-base bg-transparent cursor-pointer"
                >
                  <option value="">Select website type</option>
                  <option value="business-website">Business Website</option>
                  <option value="ecommerce">E-commerce Store</option>
                  <option value="portfolio">Portfolio Website</option>
                  <option value="landing-page">Landing Page</option>
                  <option value="blog">Blog/Content Site</option>
                  <option value="redesign">Website Redesign</option>
                </select>
              </div>

              {/* Budget Range */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-3">
                  Budget Range
                </label>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
                  {['<$250', '$250-$400', '$400-$1000', '$1000+'].map((range) => (
                    <button
                      key={range}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, budget: range }))}
                      className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                        formData.budget === range
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {range}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  Tell Us About Your Business
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-b-2 border-gray-200 focus:border-cyan-500 outline-none transition-colors text-sm sm:text-base bg-transparent resize-none"
                  placeholder="What does your business do? What are your website goals?"
                ></textarea>
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {formData.message.length}/500 characters
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm sm:text-base font-semibold rounded-full hover:shadow-lg hover:shadow-cyan-500/50 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line text-lg sm:text-xl animate-spin"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    Get Free Consultation
                    <i className="ri-send-plane-fill text-lg sm:text-xl"></i>
                  </>
                )}
              </button>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-xs sm:text-sm">
                  <i className="ri-check-line mr-2"></i>
                  Thank you! We'll contact you within 24 hours to discuss your project.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs sm:text-sm">
                  <i className="ri-error-warning-line mr-2"></i>
                  Something went wrong. Please try again.
                </div>
              )}

              <p className="text-xs text-gray-500 text-center">
                Free consultation • No obligation • Quick response
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
